import { useTheme } from "@mui/material/styles";
import {
  Box,
  MenuItem,
  Pagination as MuiPagination,
  PaginationProps as MuiPaginationProps,
  Select,
  SelectChangeEvent,
  Typography,
  useMediaQuery,
} from "@mui/material";

export type PaginationProps = Omit<
  MuiPaginationProps,
  "count" | "onChange" | "siblingCount" | "boundaryCount"
> & {
  pageSizeOptions?: number[];
  pageSize?: number;
  totalCount: number;
  page: number;
  onChange: (page: number, pageSize: number) => void;
};

export const Pagination = ({
  totalCount,
  page,
  onChange,
  pageSize,
  pageSizeOptions,
  ...restProps
}: PaginationProps) => {
  const theme = useTheme();

  const sizeOptions = pageSizeOptions || [10, 25, 50, 100];

  const currentPageSize = pageSize || sizeOptions[0];

  const count = Math.ceil(totalCount / currentPageSize);

  const pageOptions = Array.from({ length: count }, (_, i) => i + 1);

  const onlySmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const getSiblingCount = () => {
    if (onlySmallScreen) return 1;
    return 2;
  };

  const getBoundaryCount = () => {
    if (onlySmallScreen) return 0;
    return 3;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        flexWrap: "wrap",
        gap: theme.spacing(1),
      }}
    >
      <Box display={{ xs: "none", md: "flex" }} alignItems="center">
        <Typography variant="body2">Page</Typography>

        <Select
          size="small"
          sx={{
            boxShadow: "none",
            ".MuiOutlinedInput-notchedOutline": { border: "0!important" },
          }}
          value={!pageOptions.length ? "" : page}
          onChange={(event: SelectChangeEvent<number>) => {
            onChange(Number(event.target.value), currentPageSize);
          }}
        >
          {pageOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>

        <Typography variant="body2">Rows Per Page</Typography>

        <Select
          size="small"
          sx={{
            boxShadow: "none",
            ".MuiOutlinedInput-notchedOutline": { border: "0!important" },
          }}
          value={currentPageSize}
          onChange={(event: SelectChangeEvent<number>) => {
            onChange(page, Number(event.target.value));
          }}
        >
          {sizeOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>

        <Typography variant="body2">
          {(page - 1) * currentPageSize} - {(page * currentPageSize) > totalCount ? totalCount : (page * currentPageSize)} of{" "}
          {totalCount}
        </Typography>
      </Box>

      <MuiPagination
        onChange={(_event, p) => {
          onChange(p, currentPageSize);
        }}
        siblingCount={getSiblingCount()}
        boundaryCount={getBoundaryCount()}
        count={count}
        {...restProps}
        page={page}
      />
    </Box>
  );
};
